import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, RefreshCw, RotateCcw } from 'lucide-react';
import { ApiError, api } from '../../lib/api.js';
import { Badge, Button, Callout, Panel, useToast, type Tone } from '../../ui/index.js';

/**
 * Configuration → Platform → Worker (064).
 *
 * The worker does the reading, answering and sending, and nobody who looks
 * after Tributary can see its pods. This is what it reports about itself
 * every 15 seconds — which loops ran, how they went, what it last warned
 * about — and the one way to restart it from here: it finishes what it is
 * doing and exits, and a fresh one starts.
 */

type Status = 'running' | 'late' | 'silent' | 'stopped';

interface Instance {
  id: string;
  host: string;
  release: string | null;
  startedAt: string;
  lastBeatAt: string;
  status: Status;
  loops: Record<string, { lastRunAt: string; durationMs: number; failures: number }>;
  recent: { at: string; level: 'warn' | 'error'; msg: string; detail?: string; count: number }[];
  stoppedAt: string | null;
  stopReason: string | null;
}

interface Payload {
  now: string;
  instances: Instance[];
  control: {
    restart_requested_at: string | null;
    restart_requested_by: string | null;
    reload_requested_at: string | null;
    reload_requested_by: string | null;
  } | null;
  canAct: boolean;
}

const STATUS: Record<Status, { label: string; tone: Tone }> = {
  running: { label: 'running', tone: 'ok' },
  late: { label: 'not responding', tone: 'warn' },
  silent: { label: 'gone quiet', tone: 'danger' },
  stopped: { label: 'stopped', tone: 'neutral' },
};

/** What each loop is, in words. */
const LOOP: Record<string, string> = {
  'worker.mailRead': 'Reading mailboxes',
  'worker.outbox': 'Sending mail and webhooks',
  'worker.events': 'Events',
  'worker.ingest': 'Mail drop folder',
  'worker.sla': 'SLA clocks',
  'worker.autoClose': 'Closing resolved tickets',
  'worker.mining': 'Finding repeat problems',
  'worker.telemetry_prune': 'Tidying telemetry',
};

function ago(iso: string, now: number): string {
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86_400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86_400)} d ago`;
}

function uptime(iso: string, now: number): string {
  const m = Math.round((now - new Date(iso).getTime()) / 60_000);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return h < 48 ? `${h} h ${m % 60} min` : `${Math.floor(h / 24)} days`;
}

export function WorkerPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  const q = useQuery({
    queryKey: ['platform-worker'],
    queryFn: () => api.get<Payload>('/api/v1/platform/worker'),
    refetchInterval: 10_000,
  });
  const restart = useMutation({
    mutationFn: () => api.post('/api/v1/platform/worker/restart', {}),
    onSuccess: () => {
      setConfirming(false);
      toast.success('Restart requested', 'The worker finishes what it is doing and a fresh one starts, usually within a minute.');
      void qc.invalidateQueries({ queryKey: ['platform-worker'] });
    },
    onError: (err) => toast.error('Not restarted', err instanceof ApiError ? err.message : 'the request failed'),
  });
  const reload = useMutation({
    mutationFn: () => api.post('/api/v1/platform/worker/reload', {}),
    onSuccess: () => toast.success('Reload requested', 'Every worker picks up saved settings on its next check-in.'),
    onError: (err) => toast.error('Not reloaded', err instanceof ApiError ? err.message : 'the request failed'),
  });

  const d = q.data;
  const now = d ? new Date(d.now).getTime() : Date.now();
  const live = (d?.instances ?? []).filter((i) => i.status !== 'stopped');
  const running = live.filter((i) => i.status === 'running');

  return (
    <Panel
      title="Worker"
      subtitle="Reads mailboxes, answers, sends mail and runs the clocks. What it reports about itself every 15 seconds."
    >
      {q.isLoading && <p className="text-xs text-ink-tertiary">Loading…</p>}
      {q.error && <p className="text-xs text-danger">The worker status could not be loaded.</p>}

      {d && (
        <div className="space-y-4">
          {running.length === 0 && (
            <Callout tone="danger" icon={<AlertTriangle />}>
              <p className="text-xs">
                {live.length === 0
                  ? 'No worker has checked in. Nothing is reading mail, sending it or running the clocks. It may not be deployed, or it may be failing to start — that only shows in the cluster’s logs.'
                  : 'The worker has stopped checking in. Restart it below; if it does not come back, it needs someone with cluster access.'}
              </p>
            </Callout>
          )}

          {d.instances.map((w) => {
            const st = STATUS[w.status];
            const loops = Object.entries(w.loops).sort(([a], [b]) => (LOOP[a] ?? a).localeCompare(LOOP[b] ?? b));
            return (
              <div key={w.id} className="rounded-md border border-line p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={st.tone}>{st.label}</Badge>
                  <span className="font-mono text-2xs text-ink">{w.host}</span>
                  {w.release && <span className="text-2xs text-ink-tertiary">build {w.release}</span>}
                  <span className="ml-auto text-2xs text-ink-tertiary">
                    {w.status === 'stopped'
                      ? `stopped ${ago(w.stoppedAt!, now)}${w.stopReason ? ` — ${w.stopReason}` : ''}`
                      : `up ${uptime(w.startedAt, now)} · checked in ${ago(w.lastBeatAt, now)}`}
                  </span>
                </div>

                {w.status !== 'stopped' && loops.length > 0 && (
                  <table className="mt-3 w-full text-2xs">
                    <thead className="text-ink-tertiary">
                      <tr>
                        <th className="pb-1 text-left font-medium">Loop</th>
                        <th className="pb-1 text-left font-medium">Last run</th>
                        <th className="pb-1 text-right font-medium">Took</th>
                        <th className="pb-1 text-right font-medium">Failed</th>
                      </tr>
                    </thead>
                    <tbody className="nums">
                      {loops.map(([name, l]) => (
                        <tr key={name} className="border-t border-line-subtle">
                          <td className="py-1 text-ink-secondary">{LOOP[name] ?? name}</td>
                          <td className="py-1 text-ink-tertiary">{ago(l.lastRunAt, now)}</td>
                          <td className="py-1 text-right text-ink-tertiary">{l.durationMs} ms</td>
                          <td className={`py-1 text-right ${l.failures > 0 ? 'text-danger' : 'text-ink-tertiary'}`}>{l.failures}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {w.status !== 'stopped' && (
                  <div className="mt-3">
                    <div className="text-2xs font-semibold text-ink">Recent warnings and errors</div>
                    {w.recent.length === 0 ? (
                      <p className="mt-1 text-2xs text-ink-tertiary">None since it started.</p>
                    ) : (
                      <ul className="mt-1 space-y-1">
                        {w.recent.map((r, i) => (
                          <li key={i} className="text-2xs">
                            <span className={r.level === 'error' ? 'text-danger' : 'text-warn'}>{r.level}</span>{' '}
                            <span className="text-ink">{r.msg}</span>
                            {r.count > 1 && <span className="text-ink-tertiary"> ×{r.count}</span>}
                            <span className="text-ink-tertiary"> · {ago(r.at, now)}</span>
                            {r.detail && <div className="break-words font-mono text-3xs text-ink-tertiary">{r.detail}</div>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          <div className="flex flex-wrap items-center gap-2 border-t border-line-subtle pt-3">
            {!confirming ? (
              <Button
                variant="secondary"
                leading={<RotateCcw />}
                onClick={() => setConfirming(true)}
                disabled={!d.canAct}
              >
                Restart worker
              </Button>
            ) : (
              <>
                <span className="text-2xs text-ink-secondary">
                  Mail reading and sending pause for about a minute while it restarts.
                </span>
                <Button variant="primary" loading={restart.isPending} onClick={() => restart.mutate()}>
                  Restart now
                </Button>
                <Button variant="ghost" onClick={() => setConfirming(false)}>
                  Cancel
                </Button>
              </>
            )}
            <Button variant="ghost" leading={<RefreshCw />} loading={reload.isPending} onClick={() => reload.mutate()} disabled={!d.canAct}>
              Reload settings
            </Button>
            {!d.canAct && <span className="text-2xs text-ink-tertiary">Only a signed-in platform admin can restart it.</span>}
            {d.control?.restart_requested_at && (
              <span className="ml-auto text-2xs text-ink-tertiary">
                Last restart asked {ago(d.control.restart_requested_at, now)}
                {d.control.restart_requested_by ? ` by ${d.control.restart_requested_by}` : ''}
              </span>
            )}
          </div>
        </div>
      )}
    </Panel>
  );
}
